// src/view/fighterView.ts — Dövüşçü görünümü (ARCHITECTURE §1/§5.6/§9, ANIMATION §6.5–§7, DESIGN §8/§11): model + soket holder'ları +
// silah takma/çıkarma + pos/yaw interpolasyonu (alpha) + squash/stretch + hitstop titremesi + tilt/pitch/tumble pivotu + temas gölgesi
// (blob) + doğuş koruması yanıp sönme + emissive flaş + Carried soket bağlama. AnimController ARAYÜZÜNE karşı yazılır (gerçek WP7;
// burada stub üretici de var). animMap (WP7) `decide` enjeksiyonu ile gelir. Sıcak yolda tahsis yok (modül scratch'leri).
// Grup hiyerarşisi (ANIMATION §6.6): actorRoot (pos/yaw + ring-out yaw dönüşü) › spinPivot (tumble / yerde rulo pivotu) › visualRoot
// (squash ölçeği, pitch/roll, titreme) › model.
import * as THREE from 'three';
import type { AnimController, AnimDecision, AnimPlay, Fighter, Joint, PropId, SimWorld, Vec3 } from '../core/types.ts';
import { FState } from '../core/types.ts';
import { wrapAngle } from '../core/vec3.ts';
import { clamp, clamp01, degToRad, expDecay, lerp } from '../core/math.ts';
import { NO_ID, NO_TICK, TICK_DT, TICK_HZ } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import { CLIPS, canonicalOf, clipDurationOf } from '../config/clipTable.ts';
import type { CharacterDef } from '../config/characters.ts';
// ANIMATION §7 soket ölçümü (SOCKET_T/Q_L/Q_R) ve sanal soket çözümü TEK kaynakta: animController. Kopyası burada da durunca ölçüm
// güncellenirse iki yer ayrışıyordu (3. tur bulgusu); view → anim bağımlılığı ARCHITECTURE §1'de izinli.
import { resolveSockets } from '../anim/animController.ts';

/* ------------------------------------------------------------- yerel sabitler */
/** Fighter.squashKind kodları (types.ts'de sözleşme yok → WP8 yerel; sim WP2/WP3 aynı kodları yazmalı — needsWP0). Sıra FEEL alanları. */
export const SQUASH_KIND = { hit: 0, land: 1, jump: 2, thrown: 3 } as const;
/** Doğuş koruması yanıp sönme frekansı (DESIGN §8 "%50 saydam yanıp söner"; frekans belgede yok → 6 Hz, needsWP0). */
const BLINK_HZ = 6;
/** Temas gölgesi (DESIGN §8 "blob, havada küçülür"): yarıçap, saydamlık, tam kaybolma yüksekliği (belgede sayı yok → yerel). */
const BLOB_RADIUS = 0.55;
const BLOB_OPACITY = 0.38;
const BLOB_FADE_HEIGHT = 4;
/** Tumble pivotuna dönüşün süresi (KNOCKDOWN.groundPoseSlerpTicks 3 t). */
const PIVOT_RETURN_SEC = T.KNOCKDOWN.groundPoseSlerpTicks * TICK_DT;
/** `netPose` çıktısı için modül scratch'i (kare başına tahsis yok). */
const _pose = { x: 0, y: 0, z: 0, yaw: 0 };
/** Eğim rampası: λ = 1/τ, τ ≈ 0.07 s (4–5 karede oturur). Kalan fark bu derecenin altına inince hedefe yapıştırılır. */
const PITCH_LAMBDA = 14;
const PITCH_SNAP_DEG = 0.05;
/** Yerde rulo (FState.Roll) dönüş pivotu: Lie_Idle t0 pozunda kalça yüksekliği (ANIMATION §6.2 ölçüm hips 0.33). Aktör kökü (zemin)
 *  etrafında döndürülürse 90°'de yarı beden, 180°'de tüm beden yerin altına iner → dönüş kalça yüksekliğinden geçirilir
 *  (tuning'de yok → yerel, needsWP0 T.ANIM.rollPivotY). */
export const ROLL_PIVOT_Y = 0.33;
/** Rulo toplam açısı: 360° (bir tam tur; bitişte sırtüstü Lie_Idle ile süreklilik, 0°'a sıçrama yok). ANIMATION §6.2 "180°" sırtüstünü
 *  yüzüstüne çevirir ama Lying sırtüstü Lie_Idle gösterir → deviation (ANIMATION notu). animMap.rollDeg 3. turda AYNI sözleşmeye
 *  (360°, aynı işaret) çekildi — burada yine sim alanlarından (dodgeDir, stateTick, rollTicks) üretilir; sayı tuning'de tek yerde. */
export const ROLL_TOTAL_DEG: number = T.ANIM.rollTotalDeg;
/** İsabet flaşı emissive yoğunluğu. 1.0 → 0.5 yetmedi: BEYAZ emissive her materyalin base rengini/dokusunu yıkadığı için vurulan
 *  karakter yine "bembeyaz" görünüyordu (kullanıcı geri bildirimi: "çok rahatsız edici"). Üç şey birden değişti:
 *  (a) yoğunluk 0.5 → 0.16, (b) renk beyaz yerine SICAK amber (FLASH_RGB — vuruş kıvılcımıyla aynı aile, dokunun rengi kalıyor),
 *  (c) flaş sabit değil, kalan süreyle SÖNÜYOR (bkz. flashK) → 100 ms'lik plato yerine kısa bir parıltı.
 *  needsWP0: T.FEEL.flashEmissive / flashColor. */
export const FLASH_EMISSIVE = 0.16;
/** Flaş rengi (lineer RGB): sıcak amber. Beyaz (1,1,1) karakterin rengini yok ediyordu; bu ton dokunun üstüne "ısı" ekler. */
const FLASH_RGB: readonly [number, number, number] = [1, 0.62, 0.3];
/** Sim'in `flashUntil` tick'i için toplam süre (T.FEEL.flashMs = 100 ms → 6 t): sönme eğrisinin paydası. */
const FLASH_TICKS = Math.round(T.FEEL.flashMs / 1000 * TICK_HZ);

/** three, kemik adlarındaki `.` gibi karakterleri siler (PropertyBinding.sanitizeNodeName): 'handslot.l' → 'handslotl'. */
const normName = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const JOINT_BONE: Readonly<Record<Joint, string>> = {
  'hand.l': 'handl', 'hand.r': 'handr', 'foot.l': 'footl', 'foot.r': 'footr', 'toes.l': 'toesl', 'toes.r': 'toesr', head: 'head',
  'weapon.tip': 'handslotr', 'weapon.mid': 'handslotr', 'weapon.tip.l': 'handslotl', 'weapon.mid.l': 'handslotl', center: 'hips',
};

/* ------------------------------------------------------------- yardımcılar */

export const emptyPlay = (): AnimPlay => ({ clip: '', loop: false, rate: 1, fade: 0, fadeOut: 0, startAt: 0, pauseAt: -1, endAt: -1, startTick: 0 });
/** Nötr karar (stub animMap / testler): görünür, ölçek 1, bağlı değil. */
export function neutralDecision(): AnimDecision {
  return {
    base: emptyPlay(), upper: emptyPlay(), syncTime: -1, paused: false, additiveWeight: 0,
    pitchDeg: 0, rollDeg: 0, tumbleDeg: 0, squashX: 1, squashY: 1, pinHipsY: true,
    attachTo: NO_ID, attachFwd: 0, attachUp: 0, attachSide: 0, attachYawDeg: 0, visible: true, opacity: 1, shrink: 0,
  };
}
/** Stub AnimController (WP7 gelene kadar / testler): apply/update no-op, jointWorld = kök konumu (+ kaba eklem yükseklikleri). */
export function createStubController(root: THREE.Object3D): AnimController {
  const pos = new THREE.Vector3();
  return {
    apply: () => { /* no-op */ },
    update: () => { /* no-op */ },
    jointWorld: (out, joint) => {
      root.getWorldPosition(pos);
      out.x = pos.x; out.y = pos.y + (joint === 'head' ? 1.9 : joint.startsWith('foot') || joint.startsWith('toes') ? 0.1 : 1.1); out.z = pos.z;
      return out;
    },
    dispose: () => { /* no-op */ },
  };
}

/** prevPos → pos interpolasyonu (alpha 1 = son sim durumu). */
export function interpPos(out: Vec3, f: Fighter, alpha: number): Vec3 {
  out.x = lerp(f.prevPos.x, f.pos.x, alpha); out.y = lerp(f.prevPos.y, f.pos.y, alpha); out.z = lerp(f.prevPos.z, f.pos.z, alpha);
  return out;
}
/** prevYaw → yaw en kısa yoldan (snap dönüşte sarma yok). */
export const interpYaw = (f: Fighter, alpha: number): number => f.prevYaw + wrapAngle(f.yaw - f.prevYaw) * alpha;

const easeOutCubic = (p: number): number => 1 - Math.pow(1 - p, 3);
/** Squash ölçeği (DESIGN §11): hedef ölçekten 1'e out-cubic; `thrown` uçarken sabit. out = [x, y, z]. */
export function squashScale(out: [number, number, number], kind: number, remainingTicks: number): [number, number, number] {
  let target: readonly [number, number, number]; let totalMs: number;
  switch (kind) {
    case SQUASH_KIND.land: target = T.FEEL.squashLand; totalMs = T.FEEL.squashLandMs; break;
    case SQUASH_KIND.jump: target = T.FEEL.squashJump; totalMs = T.FEEL.squashJumpMs; break;
    case SQUASH_KIND.thrown: target = T.FEEL.squashThrown; totalMs = 0; break;
    default: target = T.FEEL.squashHit; totalMs = T.FEEL.squashMs; break;
  }
  if (remainingTicks <= 0) { out[0] = out[1] = out[2] = 1; return out; }
  const total = totalMs / 1000 * TICK_HZ;
  const k = totalMs === 0 ? 0 : easeOutCubic(clamp01(1 - remainingTicks / total));   // k: 0 = tam squash, 1 = normal
  out[0] = lerp(target[0], 1, k); out[1] = lerp(target[1], 1, k); out[2] = lerp(target[2], 1, k);
  return out;
}
/** Hitstop'ta titreyen KURBAN mı: kendi hitstop'u var ve yakın zamanda vuruldu (vuranın lastHitTick'i değişmez). */
export const isHitstopVictim = (f: Fighter, tick: number): boolean =>
  f.hitstop > 0 && f.lastHitTick >= tick - (T.HITSTOP.parry + 1);

/** Loko klipleri → ayak temas fazları (ANIMATION §2.1 ölçüm; view-yerel footstep, ARCHITECTURE §5.7). Anahtar KANONİK addır:
 *  temas zamanları kanonik klibe ait, türetilmiş adlar (Carry'de Walking_A#lower gibi) canonicalOf ile aynı satıra düşer. */
const FOOT_CONTACTS: ReadonlyMap<string, readonly [number, number]> = new Map([
  [CLIPS.WALKING_A, T.ANIM.footWalkA], [CLIPS.RUNNING_A, T.ANIM.footRunA], [CLIPS.RUNNING_B, T.ANIM.footRunB],
]);

/* ------------------------------------------------------------- bağımlılıklar */

export interface FighterViewDeps {
  /** Aktör kökünün ekleneceği kap (sahne). */
  scene: THREE.Object3D;
  /** AnimController üreticisi (WP7 animController; yoksa createStubController). */
  createController: (root: THREE.Object3D, f: Fighter, def: CharacterDef) => AnimController;
  /** animMap (WP7): Fighter → AnimDecision; out yerinde yazılır (tahsis yok). Yoksa nötr karar. */
  decide: ((world: SimWorld, f: Fighter, alpha: number, out: AnimDecision) => void) | null;
  /** AĞ: bu dövüşçünün ONAYLANMIŞ pozu (rakipler için). false → tahmin edilen dünyadan çiz. */
  netPose?: ((id: number, alpha: number, out: { x: number; y: number; z: number; yaw: number }) => boolean) | null;
  /** Prop örnekleme (assets.createProp). */
  createProp: (id: PropId) => THREE.Object3D;
  /** Carried bağlama için taşıyanın görünümü. */
  viewOf: (id: number) => FighterView | null;
  /** Ayak sesi (audio.footstep); side 0 = sol, 1 = sağ. */
  onFootstep: ((f: Fighter, side: 0 | 1) => void) | null;
}

const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _axis = new THREE.Vector3();
const _scale: [number, number, number] = [1, 1, 1];

/** Loko fazı `t`'yi bu karede geçti mi (prev → next; klip sonunda sarma dâhil). Modül düzeyi saf fonksiyon: sıcak yolda closure yok. */
const crossedPhase = (prev: number, next: number, dur: number, t: number): boolean =>
  (prev < t && next >= t) || (next >= dur && prev < t + dur && next - dur >= t);

/* --------------------------------------------------------------- görünüm */

export class FighterView {
  readonly id: number;
  readonly actorRoot = new THREE.Group();
  readonly spinPivot = new THREE.Group();
  readonly visualRoot = new THREE.Group();
  readonly controller: AnimController;
  readonly decision: AnimDecision = neutralDecision();
  readonly bones = new Map<string, THREE.Object3D>();
  readonly chest: THREE.Object3D | null;
  readonly socketR: THREE.Object3D | null;
  readonly socketL: THREE.Object3D | null;
  readonly holderR: THREE.Group;
  readonly holderL: THREE.Group;
  readonly materials: THREE.MeshStandardMaterial[] = [];
  readonly blob: THREE.Mesh;
  hasVirtualSocket = false;
  /** Takılı prop'lar (Item id): sağ / sol; NO_ID boş. */
  private equippedR = NO_ID; private equippedL = NO_ID;
  private propR: THREE.Object3D | null = null; private propL: THREE.Object3D | null = null;
  /** Interpolasyonlu son konum/yaw (HUD/VFX/kamera okur). */
  readonly pos: Vec3 = { x: 0, y: 0, z: 0 };
  /**
   * DENENDİ VE ÇÜRÜTÜLDÜ — görsel düzeltme yumuşatma YOK, bilerek.
   *
   * Geri sarma düzeltmelerini bir ofsete alıp eritmeyi denedik ("dünya doğru yerde, görüntü kayarak varsın").
   * Ölçüm bunun DAHA KÖTÜ olduğunu gösterdi: iki insanlı bir maçta rakibin SİM konumu 3 saniyede 1 kez yön
   * değiştiriyordu (düzgün), ama ÇİZİLEN konumu 11 kez — çünkü saniyede ~19 düzeltme geliyor ve her biri
   * öncekini eritmeye vakit bulamadan görüntüyü geri çekiyor. Yumuşatmanın kendisi lastik bandı üretiyordu.
   *
   * Sim zaten düzgün akıyor; doğru olan onu OLDUĞU GİBİ çizmek.
   */
  yaw = 0;
  attachedTo = NO_ID;
  /** view-yerel zaman (titreme/yanıp sönme fazı; sim'e girmez). */
  private time = 0;
  /** Ekranda uygulanan eğim (derece); animMap'in hedefine üstel yaklaşır — bkz. rampa notu. */
  private pitchDeg = 0;
  private flashUntilTime = -1;
  /** Son `flash(sec)` çağrısının süresi (sönme paydası). */
  private flashSpan = 1e-4;
  private lokoClip = ''; private lokoTime = 0;
  private pivotBlend = 0;   // 0 = kimlik, 1 = tumble pivotu
  private transparent = false;

  constructor(f: Fighter, readonly def: CharacterDef, readonly model: THREE.Object3D, readonly deps: FighterViewDeps) {
    this.id = f.id;
    this.actorRoot.name = 'actor_' + f.id;
    this.spinPivot.name = 'spinPivot'; this.visualRoot.name = 'visualRoot';
    this.actorRoot.add(this.spinPivot); this.spinPivot.add(this.visualRoot); this.visualRoot.add(model);
    deps.scene.add(this.actorRoot);

    // Kemikler + materyaller: SkeletonUtils.clone materyalleri paylaşır → flaş/saydamlık için görünüm başına kopya
    // (ARCHITECTURE "aynı atlas → tek materyal" prop'lar içindir; karakter flaşı görünüm başına — deviation notu).
    model.traverse((o) => {
      const b = o as THREE.Bone;
      if (b.isBone || /^(root|hips)$/i.test(o.name)) this.bones.set(normName(o.name), o);
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        const mat = m.material;
        if (Array.isArray(mat)) {
          m.material = mat.map((mm) => { const c = mm.clone() as THREE.MeshStandardMaterial; this.materials.push(c); return c; });
        } else {
          const c = mat.clone() as THREE.MeshStandardMaterial; m.material = c; this.materials.push(c);
        }
      }
    });
    this.chest = this.bones.get('chest') ?? null;
    // handslot.l/.r + holder grupları (Mannequin_Medium'da el kemiğine ölçülmüş sanal soket): animController.resolveSockets
    const sockets = resolveSockets(this.bones);
    this.socketR = sockets.r; this.socketL = sockets.l; this.hasVirtualSocket = sockets.hasVirtual;
    this.holderR = sockets.holderR; this.holderL = sockets.holderL;

    // Temas gölgesi: sahneye kardeş (aktör dönüşümünden bağımsız; zemin y'ye oturur, havada küçülür).
    const blobMat = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: BLOB_OPACITY, depthWrite: false });
    this.blob = new THREE.Mesh(new THREE.CircleGeometry(BLOB_RADIUS, 20), blobMat);
    this.blob.rotation.x = -Math.PI / 2;
    this.blob.renderOrder = -1;
    this.blob.name = 'blob_' + f.id;
    deps.scene.add(this.blob);

    this.controller = deps.createController(model, f, def);
    this.pos.x = f.pos.x; this.pos.y = f.pos.y; this.pos.z = f.pos.z; this.yaw = f.yaw;
    this.actorRoot.position.set(f.pos.x, f.pos.y, f.pos.z);
    this.actorRoot.rotation.y = f.yaw;
  }

  /* ---------------------------------------------------------------- ekipman */

  /** Prop takar (holder.add; holder yerel +Y = item.holderOffsetY — yalnız staff 0.75). */
  equip(hand: 'R' | 'L', itemId: number, propId: PropId, holderOffsetY: number): void {
    this.unequip(hand);
    const holder = hand === 'R' ? this.holderR : this.holderL;
    const prop = this.deps.createProp(propId);
    holder.position.set(0, holderOffsetY, 0);
    holder.add(prop);
    if (hand === 'R') { this.propR = prop; this.equippedR = itemId; } else { this.propL = prop; this.equippedL = itemId; }
  }
  unequip(hand: 'R' | 'L'): void {
    const prop = hand === 'R' ? this.propR : this.propL;
    if (prop) prop.removeFromParent();
    if (hand === 'R') { this.propR = null; this.equippedR = NO_ID; } else { this.propL = null; this.equippedL = NO_ID; }
  }
  get equippedItemR(): number { return this.equippedR; }
  get equippedItemL(): number { return this.equippedL; }

  /** Sim alanlarına göre takılı propları eşitler (weaponItem sağ; weaponItemL / shieldItem sol). */
  private syncEquipment(world: SimWorld, f: Fighter): void {
    const wantR = f.weaponItem;
    const wantL = f.weaponItemL !== NO_ID ? f.weaponItemL : f.shieldItem;
    if (wantR !== this.equippedR) {
      const it = wantR >= 0 ? world.items[wantR] : undefined;
      if (it) this.equip('R', wantR, it.propId, it.holderOffsetY); else this.unequip('R');
    }
    if (wantL !== this.equippedL) {
      const it = wantL >= 0 ? world.items[wantL] : undefined;
      if (it) this.equip('L', wantL, it.propId, it.holderOffsetY); else this.unequip('L');
    }
  }

  /* ---------------------------------------------------------------- his */

  /** Amber emissive flaş (sn); Ev.TumbleControl (60 ms) / Parry için dışarıdan da çağrılır; sim flashUntil ile birleşir.
   *  Süre ayrıca saklanır (flashSpan): sönme eğrisi için "ne kadarı kaldı" gerekiyor. */
  flash(sec: number): void {
    const until = this.time + sec;
    if (until >= this.flashUntilTime) { this.flashUntilTime = until; this.flashSpan = Math.max(sec, 1e-4); }
  }

  private setEmissive(intensity: number): void {
    for (const m of this.materials) {
      if (intensity > 0) { m.emissive.setRGB(FLASH_RGB[0], FLASH_RGB[1], FLASH_RGB[2]); m.emissiveIntensity = intensity; }
      else if (m.emissiveIntensity !== 0) { m.emissiveIntensity = 0; m.emissive.setRGB(0, 0, 0); }
    }
  }
  /** `holdTransparent`: doğuş koruması yanıp sönmesi boyunca (opacity 1 ↔ 0.5, 12 geçiş/s) `transparent` sabit kalsın — her geçişte
   *  ~9 materyale needsUpdate (program yeniden kurulumu, opaque/transparent liste zıplaması) olmasın; yalnız opacity yazılır. */
  private setOpacity(opacity: number, holdTransparent: boolean): void {
    const needT = opacity < 0.999 || holdTransparent;
    if (needT !== this.transparent) {
      this.transparent = needT;
      for (const m of this.materials) { m.transparent = needT; m.depthWrite = true; m.needsUpdate = true; }
    }
    if (needT) for (const m of this.materials) m.opacity = opacity;
    else for (const m of this.materials) if (m.opacity !== 1) m.opacity = 1;
  }

  /* ---------------------------------------------------------------- kare */

  /** Her render karesi (ARCHITECTURE §3): interpolasyon + animMap → controller.apply + update(dt) + sunum efektleri. */
  update(world: SimWorld, alpha: number, dt: number): void {
    const f = world.fighters[this.id];
    if (!f) return;
    this.time += dt;
    const d = this.decision;
    if (this.deps.decide) this.deps.decide(world, f, alpha, d);

    // Konum/yaw. AĞDA RAKİPLER onaylanmış geçmişten çizilir (deps.netPose): tahmin edilen dünya onları
    // 12 tick ileriden kestiriyor ve çoğu tick yanılıyor — ölçümde 5 saniyede 6 yön dönüşü, 11 sıçrama.
    // Onaylanmış geçmiş asla düzeltilmediği için asla sıçramaz. Kendi dövüşçümüz TAHMİNDEN çizilir
    // (anında tepki); `netPose` onun için zaten false döner. Çevrimdışında hep false.
    if (!this.deps.netPose || !this.deps.netPose(this.id, alpha, _pose)) {
      interpPos(this.pos, f, alpha);
      this.yaw = interpYaw(f, alpha);
    } else {
      this.pos.x = _pose.x; this.pos.y = _pose.y; this.pos.z = _pose.z;
      this.yaw = _pose.yaw;
    }
    this.syncEquipment(world, f);

    // Carried / Held: taşıyanın chest kemiğine yerel (fwd, up, side) + yaw (ANIMATION §6.5; three yerel: x = side, y = up, z = fwd)
    const holder = d.attachTo >= 0 ? this.deps.viewOf(d.attachTo) : null;
    const chest = holder && holder !== this ? holder.chest : null;
    if (chest) {
      if (this.actorRoot.parent !== chest) chest.add(this.actorRoot);
      this.attachedTo = d.attachTo;
      this.actorRoot.position.set(d.attachSide, d.attachUp, d.attachFwd);
      this.actorRoot.rotation.set(0, degToRad(d.attachYawDeg), 0);
    } else {
      if (this.actorRoot.parent !== this.deps.scene) this.deps.scene.add(this.actorRoot);
      this.attachedTo = NO_ID;
      this.actorRoot.position.set(this.pos.x, this.pos.y, this.pos.z);
      this.actorRoot.rotation.set(0, this.yaw, 0);
    }

    // Tumble pivotu (ANIMATION §6.6): girişte anında, çıkışta 3 t'de kimliğe
    const tumbling = d.tumbleDeg !== 0;
    this.pivotBlend = tumbling ? 1 : Math.max(0, this.pivotBlend - dt / PIVOT_RETURN_SEC);
    const pb = this.pivotBlend;
    // Yerde rulo (FState.Roll): açı sim'den (dodgeDir'in yerel SOL bileşeni: sola rulo → NEGATİF z dönüşü; +Z etrafında pozitif dönüş
    // +X = sol tarafı yukarı kaldırır = beden sağa yuvarlanır). Pivot kalça yüksekliği (ROLL_PIVOT_Y): spinPivot yukarı, visualRoot
    // aşağı (tumble kalıbı). 0 → 360° tek tur: giriş/çıkışta kimlik → pivot ofsetinin açılıp kapanması sıçratmaz.
    const rolling = !tumbling && f.state === FState.Roll;
    let rollRad = degToRad(d.rollDeg);
    // Ring-out düşüşü (Falling, tumble değil): DESIGN §8 / ANIMATION §2.1 540°/s DİKEY eksen (yaw) dönüşü. AnimDecision'da yaw alanı
    // olmadığından animMap bunu rollDeg'e yazıyor (varil rulosu olurdu) → burada rollDeg yok sayılır, dönüş actorRoot yaw'ına eklenir
    // (needsWP0: AnimDecision.yawDeg; o gelince animMap yawDeg yazar, burası doğrudan uygular).
    let spinY = 0;
    if (rolling) {
      const c = Math.cos(f.yaw), sn = Math.sin(f.yaw);
      const left = f.dodgeDirX * c - f.dodgeDirZ * sn;   // vec3.worldToLocalXZ z bileşeni (sol)
      const k = clamp01((world.tick - f.stateTick + alpha) / T.KNOCKDOWN.rollTicks);
      rollRad = (left >= 0 ? -1 : 1) * degToRad(ROLL_TOTAL_DEG) * k;
    } else if (!tumbling && f.state === FState.Falling) {   // tumble'lı düşüşte spinPivot dönüşü zaten sürer
      const since = f.fellAt !== NO_TICK ? f.fellAt : f.stateTick;
      spinY = degToRad(T.FEEL.fellSpinDegPerSec * (world.tick - since + alpha) * TICK_DT);
      rollRad = 0;
    }
    if (spinY !== 0 && !chest) this.actorRoot.rotation.y = this.yaw + spinY;
    const rp = rolling ? ROLL_PIVOT_Y : 0;
    this.spinPivot.position.set(0, T.ANIM.tumblePivotY * pb + rp, 0);
    const mo = T.ANIM.tumbleMeshOffset;
    if (tumbling) {
      // Sim ekseni DÜNYA uzayında (knockdown.ts: (dirZ, −dirX) = KB yönüne dik yatay); spinPivot yaw ile dönen actorRoot'un çocuğu →
      // yerel eksen = R_y(−yaw)·dünya (three R_y: local (x,z) → world (x·c + z·s, −x·s + z·c)). Doğrudan yazılsaydı eksen 2·yaw dönerdi.
      const c = Math.cos(this.yaw), sn = Math.sin(this.yaw);
      _axis.set(f.tumbleAxisX * c - f.tumbleAxisZ * sn, 0, f.tumbleAxisX * sn + f.tumbleAxisZ * c);
      if (_axis.lengthSq() < 1e-6) _axis.set(1, 0, 0); else _axis.normalize();
      this.spinPivot.quaternion.setFromAxisAngle(_axis, degToRad(d.tumbleDeg));
    } else if (rolling) {
      _axis.set(0, 0, 1);
      this.spinPivot.quaternion.setFromAxisAngle(_axis, rollRad);
    } else if (pb > 0) {
      _q.identity();
      this.spinPivot.quaternion.slerp(_q, clamp01(dt / PIVOT_RETURN_SEC));
    } else {
      this.spinPivot.quaternion.identity();
    }

    // Squash (sim squashUntil/squashKind × animMap squash) + ring-out/eleme küçülmesi (d.shrink)
    squashScale(_scale, f.squashKind, f.squashUntil - world.tick);
    const shrink = 1 - clamp01(d.shrink);
    this.visualRoot.scale.set(_scale[0] * d.squashX * shrink, _scale[1] * d.squashY * shrink, _scale[2] * d.squashX * shrink);

    // Tilt (pitch +: öne eğilme; Launch −15…−50 arkaya) + hitstop titremesi (yalnız kurban; DESIGN §11 ±0.06 u 40 Hz)
    // Eğim RAMPALANIR: hedef değeri tek karede uygulamak havada darbe yiyince gözü rahatsız eden ani bir kopma
    // yapıyordu (kullanıcı bulgusu). ~70 ms'lik üstel yaklaşma 4–5 karede oturur; "seri" hissi bozulmaz ama sıçrama gider.
    // Hitstop'ta rampa DURUR: donmuş karede beden dönmeye devam ederse donma okunmaz olur.
    const frozen = isHitstopVictim(f, world.tick);
    this.pitchDeg = frozen ? this.pitchDeg : expDecay(this.pitchDeg, d.pitchDeg, PITCH_LAMBDA, dt);
    if (Math.abs(this.pitchDeg - d.pitchDeg) < PITCH_SNAP_DEG) this.pitchDeg = d.pitchDeg;
    this.visualRoot.rotation.set(degToRad(this.pitchDeg), 0, rolling ? 0 : rollRad);
    let jx = 0, jy = 0;
    if (isHitstopVictim(f, world.tick)) {
      const ph = this.time * T.FEEL.jitterHz * Math.PI * 2;
      jx = Math.sin(ph) * T.FEEL.jitterAmp; jy = Math.cos(ph * 0.7) * T.FEEL.jitterAmp * 0.5;
    }
    this.visualRoot.position.set(mo[0] * pb + jx, mo[1] * pb - rp + jy, mo[2] * pb);

    // Flaş (sim flashUntil tick + view flash sn) ve saydamlık (doğuş koruması %50 yanıp sönme × karar opacity)
    // Sabit plato yerine KALAN SÜREYLE sönüyor: aynı 100 ms içinde göz yalnız ilk karelerde parlamayı görür, karakterin rengi
    // hemen geri gelir. İki kaynak (sim tick'i / view saniyesi) birbirini ezmesin diye max alınır.
    let flashK = 0;
    if (f.flashUntil > world.tick) flashK = clamp01((f.flashUntil - world.tick) / FLASH_TICKS);
    if (this.flashUntilTime > this.time) flashK = Math.max(flashK, clamp01((this.flashUntilTime - this.time) / this.flashSpan));
    this.setEmissive(FLASH_EMISSIVE * flashK);
    let opacity = d.opacity;
    const protecting = f.spawnProtUntil > world.tick;
    if (protecting && Math.sin(this.time * BLINK_HZ * Math.PI * 2) > 0) opacity *= T.RESPAWN.protectionBlinkAlpha;
    this.setOpacity(opacity, protecting);
    this.actorRoot.visible = d.visible && f.state !== FState.Eliminated || (d.visible && d.shrink < 1);

    // Temas gölgesi: yürünebilir zemin altında, yükseklikle küçülür; dışarıda/bağlıyken gizli
    const gy = chest ? NaN : world.query.groundY(this.pos.x, this.pos.z, this.pos.y + 0.5);
    if (Number.isNaN(gy) || !this.actorRoot.visible) {
      this.blob.visible = false;
    } else {
      const h = Math.max(0, this.pos.y - gy);
      const k = clamp(1 - h / BLOB_FADE_HEIGHT, 0, 1);
      this.blob.visible = k > 0.02;
      this.blob.position.set(this.pos.x, gy + 0.01, this.pos.z);
      this.blob.scale.set(k * shrink, k * shrink, 1);
      (this.blob.material as THREE.MeshBasicMaterial).opacity = BLOB_OPACITY * k;
    }

    // Animasyon
    this.controller.apply(d);
    this.controller.update(dt);
    this.stepFootsteps(f, d, dt);
  }

  /** Ayak sesi: loko klip fazından (ANIMATION §2.1 temas zamanları); sim olayı değil (ARCHITECTURE §5.7). */
  private stepFootsteps(f: Fighter, d: AnimDecision, dt: number): void {
    const cb = this.deps.onFootstep;
    const clip = d.base.clip;
    const contacts = clip ? FOOT_CONTACTS.get(canonicalOf(clip)) : undefined;
    if (!contacts || !cb) { this.lokoClip = ''; return; }
    const dur = clipDurationOf(clip);
    if (clip !== this.lokoClip) {
      // Klip değişti: iki taraf da loko ise animController FAZ AKTARIMI yapıyor (next.time = prev.time / prev.dur · next.dur) →
      // ayak sesi fazı da aynı kuralla taşınmalı, yoksa Walk↔Run / RunA↔RunB geçişinde ses görünen adımdan ~yarım periyot kayar.
      const prevDur = this.lokoClip ? clipDurationOf(this.lokoClip) : 0;
      this.lokoTime = prevDur > 0 ? clamp(this.lokoTime / prevDur * dur, 0, dur) : d.base.startAt;
      this.lokoClip = clip;
      return;
    }
    if (d.paused || !f.grounded) return;
    const prev = this.lokoTime;
    let next = prev + dt * d.base.rate;
    if (crossedPhase(prev, next, dur, contacts[0])) cb(f, 0);
    if (crossedPhase(prev, next, dur, contacts[1])) cb(f, 1);
    if (next >= dur) next -= dur;
    this.lokoTime = next;
  }

  /* ---------------------------------------------------------------- okuma */

  /** Loko klip fazı 0..1 (ayak sesi zaman tabanı; klip değişiminde faz aktarımının testi/debug'ı için). */
  get lokoPhase(): number { const dur = this.lokoClip ? clipDurationOf(this.lokoClip) : 0; return dur > 0 ? this.lokoTime / dur : 0; }
  /** Eklem dünya konumu (VFX izleri; controller.jointWorld). */
  jointWorld(out: Vec3, joint: Joint): Vec3 { return this.controller.jointWorld(out, joint); }
  /** Kemik nesnesi (debug / soket); yoksa null. */
  bone(joint: Joint): THREE.Object3D | null { return this.bones.get(JOINT_BONE[joint]) ?? null; }
  /** Aktör kökünün dünya konumu (bağlıyken matrixWorld'den — bir kare gecikmeli kabul). */
  worldPos(out: Vec3): Vec3 {
    if (this.attachedTo === NO_ID) { out.x = this.pos.x; out.y = this.pos.y; out.z = this.pos.z; return out; }
    this.actorRoot.getWorldPosition(_v);
    out.x = _v.x; out.y = _v.y; out.z = _v.z; return out;
  }
  /** Baş üstü etiket dünya noktası (HUD): kök + katalog boyu. */
  headWorld(out: Vec3): Vec3 { this.worldPos(out); out.y += this.def.height * this.visualRoot.scale.y; return out; }

  dispose(): void {
    this.controller.dispose();
    this.unequip('R'); this.unequip('L');
    this.actorRoot.removeFromParent();
    this.blob.removeFromParent();
    this.blob.geometry.dispose();
    (this.blob.material as THREE.Material).dispose();
    for (const m of this.materials) m.dispose();
    // SkeletonUtils.clone her skinned mesh'e ayrı Skeleton (bone DataTexture) verir → bırakılmazsa GPU'da birikir (viewer dersi).
    this.model.traverse((o) => { const s = o as THREE.SkinnedMesh; if (s.isSkinnedMesh) s.skeleton?.dispose(); });
  }
}
